import { SpecificOrAnyString } from "@jugl-web/utils";
import { omitUndefined } from "@jugl-web/utils/utils/omitUndefined";
import { EntityState } from "@reduxjs/toolkit";
import add from "date-fns/add";
import sub from "date-fns/sub";
import {
  PaginatedRequestParams,
  PaginatedResponse,
  RtkBaseQuery,
  RtkEmptySplitApi,
} from "../../types";
import { fetchAllPages } from "../../utils";
import { DEFAULT_LABEL_COLOR } from "./consts";
import { transformArchiveTasksDtoToModel } from "./models/ArchiveTasks";
import { BasicTaskBoardModel } from "./models/BasicTaskBoard";
import { BasicTaskBoardDto } from "./models/BasicTaskBoard/BasicTaskBoardDto";
import {
  CustomerTaskModel,
  transformCustomerTaskDtoToModel,
} from "./models/CustomerTask";
import { CustomerTaskDto } from "./models/CustomerTask/CustomerTaskDto";
import {
  ExportedTaskModel,
  transformExportedTaskDtoToModel,
} from "./models/ExportedTask";
import { ExportedTaskDto } from "./models/ExportedTask/ExportedTaskDto";
import { GuestTaskDataModel } from "./models/GuestTaskData";
import { GuestTaskDataDto } from "./models/GuestTaskData/GuestTaskDataDto";
import { transformGuestDataDtoToModel } from "./models/GuestTaskData/transformGuestTaskDataDtoToModel";
import { ImportedTaskModel } from "./models/ImportedTask/ImportedTaskModel";
import {
  PreviewTaskBoardModel,
  transformPreviewTaskBoardDtoToModel,
  transformPreviewTaskBoardModelToTaskBoardModel,
} from "./models/PreviewTaskBoard";
import { PreviewTaskBoardDto } from "./models/PreviewTaskBoard/PreviewTaskBoardDto";
import {
  CreatedSubtaskDto,
  SubtaskDto,
  UpdatedSubtaskDto,
} from "./models/Subtask/SubtaskDto";
import {
  CreateSubtaskModel,
  SubtaskModel,
} from "./models/Subtask/SubtaskModel";
import {
  transformCreateSubtaskModelToDto,
  transformCreatedSubtaskDtoToSubtaskModel,
  transformSubtaskDtoToModel,
  transformSubtaskModelToUpdateSubtaskDto,
  transformUpdatedSubtaskDtoToSubtaskModel,
} from "./models/Subtask/utils";
import {
  TaskBoardModel,
  transformPartialTaskBoardDtoToPartialTaskBoardModel,
  transformTaskBoardDtoToModel,
} from "./models/TaskBoard";
import {
  CreateTaskBoardPayload,
  TaskBoardDto,
  UpdateTaskBoardPayload,
} from "./models/TaskBoard/TaskBoardDto";
import { TaskCustomFieldDto } from "./models/TaskCustomField/TaskCustomFieldDto";
import { transformTaskCustomFieldDtoToTaskCustomFieldModel } from "./models/TaskCustomField/utils";
import { TaskCustomStatusDto } from "./models/TaskCustomStatus/TaskCustomStatusDto";
import {
  ActiveTaskTimeLogDto,
  MyTimeLogsSummaryDto,
  TaskTimeLogDto,
  TimeLogListItemDto,
} from "./models/TaskTimeLog/TaskTimeLogDto";
import {
  ActiveTaskTimeLogModel,
  CreateTaskTimeLogModel,
  MyTimeLogsSummaryModel,
  TaskTimeLogModel,
  TimeLogListItemModel,
  UpdateTaskTimeLogModel,
} from "./models/TaskTimeLog/TaskTimeLogModel";
import {
  transformActiveTaskTimeLogDtoToModel,
  transformCreateTaskTimeLogModelToDto,
  transformMyTimeLogsSummaryDtoToModel,
  transformTaskTimeLogDtoToModel,
  transformTimeLogListItemDtoToModel,
  transformUpdateTaskTimeLogModelToDto,
} from "./models/TaskTimeLog/utils";
import { ExportedTimeLogs } from "./models/common-types/ExportedTimelogs";
import { TasksApiTag } from "./tags";
import {
  ArchivedDetailedTask,
  ArchivedPreviewTask,
  BoardTemplateDetails,
  BoardTemplatesCategories,
  BoardTemplatesList,
  ChecklistSummary,
  CreateTaskCustomFieldPayload,
  CreateTaskPayload,
  DetailedTask,
  ExportBulkActionTasksParams,
  ExportTasksParams,
  ExportTasksTimeLogsParams,
  InternalTaskFilters,
  PreviewTask,
  TaskActivity,
  TaskAttachment,
  TaskFilterSet,
  TaskInitData,
  TaskLabel,
  TasksInitData,
  TasksSource,
} from "./types";
import {
  adaptDetailedTaskToPreviewTask,
  adaptInternalTaskFiltersToTaskFilters,
  adaptTaskFilterSetToInternalTaskFilterSet,
  filterOutInvalidTaskLikeObjects,
  getBoardByEntityIdTag,
  getTaskByBoardIdTag,
  getTaskByEntityIdTag,
  getTaskByIdTag,
  makeCustomFieldsSafe,
  previewTasksAdapter,
  sortCollectionByOrder,
  stripTaskDescriptionPrefix,
  withPrefixedTaskDescription,
} from "./utils";

export const addTasksApi = (emptySplitApi: RtkEmptySplitApi) => {
  const apiWithTags = emptySplitApi.enhanceEndpoints({
    addTagTypes: [
      TasksApiTag.task,
      TasksApiTag.taskAttachments,
      TasksApiTag.taskActivity,
      TasksApiTag.taskFields,
      TasksApiTag.board,
      TasksApiTag.subtask,
      TasksApiTag.subtaskAttachments,
      TasksApiTag.timeLog,
      TasksApiTag.activeTimeLog,
      TasksApiTag.myTimeLogsSummary,
    ],
  });

  const tasksApi = apiWithTags.injectEndpoints({
    endpoints: (builder) => ({
      // #region tasks
      getTasks: builder.query<
        EntityState<PreviewTask>,
        { entityId: string; source: TasksSource }
      >({
        query: ({ entityId, source }) => {
          let sourceParams: Record<string, string>;

          if (source.type === "boardTasks") {
            if (source.boardId === "my") {
              sourceParams = {};
            } else if (source.boardId === "team") {
              sourceParams = { view: "team" };
            } else {
              sourceParams = { board: source.boardId };
            }
          } else {
            sourceParams = { customer: source.customerId };
          }

          return {
            url: `/api/auth/entity/${entityId}/v2/task`,
            params: {
              // Temporary solution to get the future task occurrence dates
              // generated by the backend for a year ahead
              from: sub(new Date(), { years: 2 }).toISOString(),
              to: add(new Date(), { years: 1 }).toISOString(),
              ...sourceParams,
            },
          };
        },
        transformResponse: (response: PreviewTask[]) => {
          response.forEach(makeCustomFieldsSafe);
          return previewTasksAdapter.addMany(
            previewTasksAdapter.getInitialState(),
            filterOutInvalidTaskLikeObjects(response)
          );
        },
        providesTags: (_result, _error, { entityId, source }) => {
          const tags = [getTaskByEntityIdTag(entityId)];

          if (source.type === "boardTasks") {
            tags.push(getTaskByBoardIdTag(source.boardId));
          }

          return tags;
        },
      }),

      getTask: builder.query<
        DetailedTask,
        { entityId: string; taskId: string; noCache?: true }
      >({
        query: ({ entityId, taskId }) => ({
          url: `/api/auth/entity/${entityId}/task/${taskId}`,
          silentError: true,
        }),
        transformResponse: (task: DetailedTask) => {
          if (task.due_at && !task.due_at?.endsWith("Z")) {
            task.due_at = `${task.due_at}Z`;
          }

          if (task.desc) {
            task.desc = stripTaskDescriptionPrefix(task.desc);
          }

          if (task.custom_fields) {
            makeCustomFieldsSafe(task);
          }

          return task;
        },
        providesTags: (task) => {
          if (!task) {
            return [];
          }

          const tags = [getTaskByIdTag(task.id)];

          if (task.board_id) {
            tags.push(getTaskByBoardIdTag(task.board_id));
          }

          return tags;
        },
      }),

      getArchivedDetailedTask: builder.query<
        ArchivedDetailedTask,
        { entityId: string; taskId: string }
      >({
        query: ({ entityId, taskId }) => ({
          url: `/api/auth/entity/${entityId}/task_archive/${taskId}`,
        }),
        transformResponse: (task: ArchivedDetailedTask) => {
          if (task.due_at && !task.due_at?.endsWith("Z")) {
            task.due_at = `${task.due_at}Z`;
          }

          if (task.desc) {
            task.desc = stripTaskDescriptionPrefix(task.desc);
          }

          if (task.custom_fields) {
            makeCustomFieldsSafe(task);
          }

          return task;
        },
      }),

      getArchivedTasks: builder.query<
        PaginatedResponse<ArchivedPreviewTask>,
        PaginatedRequestParams<{
          entityId: string;
          params?: {
            assignees?: string;
            labels?: string;
            search_term?: string;
          };
        }>
      >({
        query: ({ entityId, params, ...pageParams }) => ({
          url: `/api/auth/entity/${entityId}/task_archive`,
          params: {
            ...params,
            ...pageParams,
          },
        }),
        transformResponse: (
          response: PaginatedResponse<ArchivedPreviewTask>
        ) => ({
          ...response,
          data: response.data.map((task) =>
            transformArchiveTasksDtoToModel(task)
          ),
        }),
        providesTags: (_result, _error, { entityId }) => [
          getTaskByEntityIdTag(entityId),
        ],
      }),

      archiveTask: builder.mutation<
        null,
        { entityId: string; taskId: string; source?: TasksSource }
      >({
        query: ({ entityId, taskId }) => ({
          url: `/api/auth/entity/${entityId}/task/${taskId}/archive`,
          method: "PUT",
        }),
        onQueryStarted: (args, { dispatch, queryFulfilled }) => {
          if (!args.source) {
            return;
          }

          const updateTasksCollectionPatchResult = dispatch(
            tasksApi.util.updateQueryData(
              "getTasks",
              { entityId: args.entityId, source: args.source },
              (state) => {
                previewTasksAdapter.removeOne(state, args.taskId);
              }
            )
          );

          queryFulfilled.catch(updateTasksCollectionPatchResult.undo);
        },
        invalidatesTags: [TasksApiTag.task, TasksApiTag.activeTimeLog],
      }),

      markTaskAsViewed: builder.mutation<
        null,
        { entityId: string; taskId: string }
      >({
        query: ({ entityId, taskId }) => ({
          url: `/api/auth/entity/${entityId}/task/${taskId}`,
          params: { user_viewed: true },
          silentError: true,
        }),
      }),

      sendUpdateToCustomer: builder.mutation<
        null,
        { entityId: string; taskId: string }
      >({
        query: ({ entityId, taskId }) => ({
          method: "GET",
          url: `/api/auth/entity/${entityId}/v2/task/share_link/${taskId}`,
        }),
      }),

      getCustomerTasks: builder.query<
        PaginatedResponse<CustomerTaskModel>,
        {
          entityId: string;
          params: PaginatedRequestParams<{
            phone_number?: string;
            customer?: string;
          }>;
        }
      >({
        query: ({ entityId, params }) => ({
          url: `/api/auth/entity/${entityId}/customer/task`,
          method: "POST",
          data: {
            ...params,
          },
        }),
        transformResponse: (response: PaginatedResponse<CustomerTaskDto>) => ({
          ...response,
          data: response.data.map(transformCustomerTaskDtoToModel),
        }),
      }),

      createTask: builder.mutation<
        DetailedTask,
        {
          entityId: string;
          task: CreateTaskPayload;
          source?: TasksSource;
          bulk_action?: boolean;
        }
      >({
        query: ({ entityId, task, bulk_action }) => ({
          url: `/api/auth/entity/${entityId}/task`,
          method: "POST",
          data: { ...withPrefixedTaskDescription(task), bulk_action },
        }),
        onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
          try {
            const response = await queryFulfilled;

            if (args.source) {
              const updateTasksCollectionAction = tasksApi.util.updateQueryData(
                "getTasks",
                { entityId: args.entityId, source: args.source },
                (state) => {
                  previewTasksAdapter.addOne(
                    state,
                    adaptDetailedTaskToPreviewTask(response.data)
                  );
                }
              );

              dispatch(updateTasksCollectionAction);
            }
          } catch {
            // Do nothing
          }
        },
      }),

      updateTask: builder.mutation<
        DetailedTask,
        {
          entityId: string;
          taskId: string;
          attributes: Partial<DetailedTask>;
          source: TasksSource;
          bulk_action?: boolean;
        }
      >({
        query: ({ entityId, taskId, attributes, bulk_action }) => ({
          url: `/api/auth/entity/${entityId}/task/${taskId}`,
          method: "PUT",
          data: { ...withPrefixedTaskDescription(attributes), bulk_action },
          silentError: true,
        }),
        onQueryStarted: (args, { dispatch, queryFulfilled }) => {
          const internalTaskChanges: Partial<DetailedTask> = (() => {
            const internalUpdatedAt = new Date().toISOString().split(".")[0];

            const changes: Partial<DetailedTask> = {
              ...args.attributes,
              updated_at: internalUpdatedAt,
            };

            if (args.attributes.recurrence === null) {
              return Object.assign<
                Partial<DetailedTask>,
                Partial<DetailedTask>
              >(changes, { recurrences: [] });
            }

            return changes;
          })();

          const detailedTaskPatchResult = dispatch(
            tasksApi.util.updateQueryData(
              "getTask",
              { entityId: args.entityId, taskId: args.taskId },
              (taskDraft) => {
                Object.assign(taskDraft, internalTaskChanges);
              }
            )
          );

          const previewTasksPatchResult = dispatch(
            tasksApi.util.updateQueryData(
              "getTasks",
              { entityId: args.entityId, source: args.source },
              (state) => {
                if (!state) return;
                // Remove task from the collection if it's been moved to
                // another board and the source points to different user board
                // (doesn't apply for My/Team tasks)
                if (
                  args.attributes.board_id &&
                  args.source.type === "boardTasks" &&
                  args.source.boardId !== "my" &&
                  args.source.boardId !== "team"
                ) {
                  previewTasksAdapter.removeOne(state, args.taskId);
                  return;
                }

                // Otherwise, update the task in the collection
                previewTasksAdapter.updateOne(state, {
                  id: args.taskId,
                  changes: internalTaskChanges,
                });
              }
            )
          );

          queryFulfilled.catch(() => {
            detailedTaskPatchResult.undo();
            previewTasksPatchResult.undo();
          });
        },
      }),

      deleteTask: builder.mutation<
        void,
        {
          entityId: string;
          taskId: string;
          source?: TasksSource;
          bulk_action?: boolean;
        }
      >({
        query: ({ entityId, taskId, bulk_action }) => ({
          url: `/api/auth/entity/${entityId}/task/${taskId}`,
          method: "DELETE",
          data: {
            bulk_action,
          },
        }),
        onQueryStarted: (args, { dispatch, queryFulfilled }) => {
          if (!args.source) {
            return;
          }

          const updateTasksCollectionPatchResult = dispatch(
            tasksApi.util.updateQueryData(
              "getTasks",
              { entityId: args.entityId, source: args.source },
              (state) => {
                previewTasksAdapter.removeOne(state, args.taskId);
              }
            )
          );

          queryFulfilled.catch(updateTasksCollectionPatchResult.undo);
        },
        invalidatesTags: [TasksApiTag.task, TasksApiTag.activeTimeLog],
      }),

      duplicateTask: builder.mutation<
        DetailedTask,
        {
          entityId: string;
          taskId: string;
          isBulkAction: boolean;
          source: TasksSource;
        }
      >({
        query: ({ entityId, taskId, isBulkAction }) => ({
          url: `/api/auth/entity/${entityId}/task/${taskId}/duplicate`,
          method: "POST",
          data: { bulk_action: isBulkAction },
        }),
        onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
          try {
            const response = await queryFulfilled;

            if (args.source) {
              const updateTasksCollectionAction = tasksApi.util.updateQueryData(
                "getTasks",
                { entityId: args.entityId, source: args.source },
                (state) => {
                  previewTasksAdapter.addOne(
                    state,
                    adaptDetailedTaskToPreviewTask(response.data)
                  );
                }
              );

              dispatch(updateTasksCollectionAction);
            }
          } catch {
            // Do nothing
          }
        },
      }),

      deleteArchivedTask: builder.mutation<
        void,
        { entityId: string; taskId: string }
      >({
        query: ({ entityId, taskId }) => ({
          url: `/api/auth/entity/${entityId}/task_archive/${taskId}`,
          method: "DELETE",
        }),
      }),

      restoreArchivedTask: builder.mutation<
        void,
        { entityId: string; taskId: string }
      >({
        query: ({ entityId, taskId }) => ({
          url: `/api/auth/entity/${entityId}/task_archive/${taskId}/restore`,
          method: "PUT",
          data: { id: taskId },
        }),
      }),

      exportTimeLogs: builder.query<
        { data: ExportedTimeLogs[] },
        {
          entityId: string;
          data?: ExportTasksTimeLogsParams;
        }
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/entity/${entityId}/task/export`,
          method: "POST",
          data,
        }),
      }),

      exportTasks: builder.query<
        ExportedTaskModel[],
        {
          entityId: string;
          params?: ExportTasksParams;
        }
      >({
        query: ({ entityId, params }) => ({
          url: `/api/auth/entity/${entityId}/task/export`,
          params: {
            ...params,
          },
        }),
        transformResponse: ({ data }: { data: ExportedTaskDto[] }) =>
          data.map(transformExportedTaskDtoToModel),
      }),

      exportBulkActionTasks: builder.query<
        ExportedTaskModel[],
        {
          entityId: string;
          data: ExportBulkActionTasksParams;
        }
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/entity/${entityId}/task/export`,
          method: "POST",
          data: {
            ...data,
          },
        }),
        transformResponse: ({ data }: { data: ExportedTaskDto[] }) =>
          data.map(transformExportedTaskDtoToModel),
      }),

      readTask: builder.mutation<
        null,
        { entityId: string; taskId: string; source: TasksSource }
      >({
        queryFn: () => ({ data: null }),
        onQueryStarted: (args, { dispatch }) => {
          dispatch(
            tasksApi.util.updateQueryData(
              "getTasks",
              { entityId: args.entityId, source: args.source },
              (state) => {
                previewTasksAdapter.updateOne(state, {
                  id: args.taskId,
                  changes: { unread: false },
                });
              }
            )
          );
        },
      }),

      importTasks: builder.mutation<
        {
          tasks_created: number;
          duplicates: number;
        },
        {
          entityId: string;
          data: {
            board_id: string;
            tasks: ImportedTaskModel[];
            chk_duplicate_by?: string;
          };
        }
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/entity/${entityId}/task/import`,
          method: "POST",
          data,
          silentError: true,
        }),
        invalidatesTags: (_result, _error, { entityId }) => [
          TasksApiTag.taskFields,
          getTaskByEntityIdTag(entityId),
        ],
      }),
      // #endregion

      // #region task attachments
      getTaskAttachments: builder.query<
        TaskAttachment[],
        { entityId: string; taskId: string; subtaskId?: string }
      >({
        query: ({ entityId, taskId, subtaskId }) => ({
          url: `/api/auth/entity/${entityId}/task/attachments`,
          params: {
            type: subtaskId ? "subtask" : "task",
            task_id: taskId,
            subtask_id: subtaskId,
          },
        }),
        transformResponse: (response: { data: TaskAttachment[] }) =>
          response.data,
        providesTags: (_result, _error, { subtaskId }) =>
          subtaskId
            ? [TasksApiTag.subtaskAttachments]
            : [TasksApiTag.taskAttachments],
      }),

      renameTaskAttachment: builder.mutation<
        void,
        {
          entityId: string;
          taskId: string;
          attachmentId: string;
          attachmentName: string;
          subtaskId?: string;
        }
      >({
        query: ({ entityId, taskId, attachmentId, attachmentName }) => ({
          url: `/api/auth/drive/entity/${entityId}/rename`,
          method: "PUT",
          data: {
            name: attachmentName,
            taskId,
            id: attachmentId,
          },
        }),
      }),

      deleteTaskAttachment: builder.mutation<
        void,
        {
          entityId: string;
          taskId: string;
          attachmentId: string;
          subtaskId?: string;
        }
      >({
        query: ({ entityId, attachmentId }) => ({
          url: `/api/auth/drive/entity/${entityId}/${attachmentId}`,
          method: "DELETE",
        }),
      }),
      // #endregion

      // #region subtasks
      getSubtasks: builder.query<
        PaginatedResponse<SubtaskModel>,
        PaginatedRequestParams<{
          entityId: string;
          taskId: string;
          taskVersion: number;
          searchQuery?: string;
          openOnly?: boolean;
        }>
      >({
        query: ({
          entityId,
          taskId,
          taskVersion,
          searchQuery,
          openOnly,
          ...params
        }) => ({
          url: `/api/auth/entity/${entityId}/task/${taskId}/subtask`,
          params: {
            search_term: searchQuery ? `%${searchQuery}%` : undefined,
            status: openOnly ? "open" : undefined,
            version: taskVersion,
            ...params,
          },
        }),
        transformResponse: (response: PaginatedResponse<SubtaskDto>) => ({
          ...response,
          data: response.data.map(transformSubtaskDtoToModel),
        }),
      }),

      addSubtask: builder.mutation<
        SubtaskModel,
        { entityId: string; taskId: string; model: CreateSubtaskModel }
      >({
        query: ({ entityId, taskId, model }) => ({
          url: `/api/auth/entity/${entityId}/task/${taskId}/subtask`,
          method: "POST",
          data: transformCreateSubtaskModelToDto(model),
        }),
        transformResponse: (response: CreatedSubtaskDto) =>
          transformCreatedSubtaskDtoToSubtaskModel(response),
      }),

      updateSubtask: builder.mutation<
        SubtaskModel,
        {
          entityId: string;
          taskId: string;
          subtaskId: string;
          updatedModel: SubtaskModel;
        }
      >({
        query: ({ entityId, taskId, subtaskId, updatedModel }) => ({
          url: `/api/auth/entity/${entityId}/task/${taskId}/subtask/${subtaskId}`,
          method: "PUT",
          data: transformSubtaskModelToUpdateSubtaskDto(updatedModel),
        }),
        transformResponse: (response: UpdatedSubtaskDto, _, { updatedModel }) =>
          transformUpdatedSubtaskDtoToSubtaskModel(response, updatedModel),
      }),

      moveSubtasks: builder.mutation<
        void,
        {
          entityId: string;
          taskId: string;
          subtaskIds: string[];
          targetTaskId: string;
        }
      >({
        query: ({ entityId, taskId, subtaskIds, targetTaskId }) => ({
          url: `/api/auth/entity/${entityId}/task/${taskId}/subtask/move`,
          method: "PUT",
          data: {
            to_task_id: targetTaskId,
            subtask_ids: subtaskIds,
          },
        }),
      }),

      reorderSubtasks: builder.mutation<
        DetailedTask,
        {
          entityId: string;
          taskId: string;
          payload: { id: string; order: number }[];
        }
      >({
        query: ({ entityId, taskId, payload }) => ({
          url: `/api/auth/entity/${entityId}/task/${taskId}`,
          method: "PUT",
          data: {
            checklist: payload,
          },
          silentError: true,
        }),
      }),

      deleteSubtasks: builder.mutation<
        void,
        {
          entityId: string;
          taskId: string;
          subtaskIds: string[];
        }
      >({
        query: ({ entityId, taskId, subtaskIds }) => ({
          url: `/api/auth/entity/${entityId}/task/${taskId}/subtask`,
          method: "DELETE",
          data: {
            subtask_ids: subtaskIds,
          },
        }),
      }),
      // #endregion

      // #region task fields
      taskInit: builder.query<TaskInitData, { entityId: string }>({
        query: ({ entityId }) => ({
          url: `/api/auth/entity/${entityId}/v2/task/init`,
        }),
        transformResponse: (response: TasksInitData) => ({
          customFieldsData: {
            id: response.custom_fields.id,
            items: sortCollectionByOrder(
              response.custom_fields.props.value
            ).map(transformTaskCustomFieldDtoToTaskCustomFieldModel),
          },
          labels: sortCollectionByOrder(
            response.labels.map((label) => ({
              ...label,
              color: label.color || DEFAULT_LABEL_COLOR,
            }))
          ),
          customStatuses: sortCollectionByOrder(response.custom_statuses),
          filterSets: response.filter_sets.map(
            adaptTaskFilterSetToInternalTaskFilterSet
          ),
        }),
        providesTags: [TasksApiTag.taskFields],
      }),

      createTaskCustomField: builder.mutation<
        TaskCustomFieldDto,
        {
          entityId: string;
          boardId: string;
          customField: CreateTaskCustomFieldPayload;
        }
      >({
        query: ({ entityId, boardId, customField }) => ({
          url: `/api/auth/${entityId}/board/${boardId}/custom_fields`,
          method: "POST",
          data: customField,
          silentError: true,
        }),
        onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
          try {
            const { data: customField } = await queryFulfilled;

            dispatch(
              tasksApi.util.updateQueryData(
                "getUserTaskBoards",
                { entityId: args.entityId },
                (draftState) => {
                  const matchingBoard = draftState.find(
                    (board) => board.id === args.boardId
                  );

                  if (!matchingBoard) {
                    return;
                  }

                  matchingBoard.customFields.push(
                    transformTaskCustomFieldDtoToTaskCustomFieldModel(
                      customField
                    )
                  );
                }
              )
            );
          } catch {
            // Do nothing
          }
        },
      }),

      updateTaskCustomField: builder.mutation<
        TaskCustomFieldDto,
        {
          entityId: string;
          boardId: string;
          updatedCustomField: TaskCustomFieldDto;
          optimistic?: boolean;
        }
      >({
        query: ({ entityId, boardId, updatedCustomField }) => ({
          url: `/api/auth/${entityId}/board/${boardId}/custom_fields/${updatedCustomField.id}`,
          method: "PUT",
          data: updatedCustomField,
        }),
        onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
          const replaceCachedField = (customFieldDto: TaskCustomFieldDto) => {
            const patchResult = dispatch(
              tasksApi.util.updateQueryData(
                "getUserTaskBoards",
                { entityId: args.entityId },
                (draftState) => {
                  const matchingBoard = draftState.find(
                    (board) => board.id === args.boardId
                  );

                  if (!matchingBoard) {
                    return;
                  }

                  matchingBoard.customFields = matchingBoard.customFields.map(
                    (field) =>
                      field.id === args.updatedCustomField.id
                        ? transformTaskCustomFieldDtoToTaskCustomFieldModel(
                            customFieldDto
                          )
                        : field
                  );
                }
              )
            );

            return patchResult;
          };

          if (args.optimistic) {
            const patchResult = replaceCachedField(args.updatedCustomField);
            queryFulfilled.catch(patchResult.undo);
            return;
          }

          try {
            const { data: customField } = await queryFulfilled;
            replaceCachedField(customField);
          } catch {
            // Do nothing
          }
        },
      }),

      updateTaskCustomFieldsInBulk: builder.mutation<
        TaskCustomFieldDto[],
        {
          entityId: string;
          boardId: string;
          customFields: TaskCustomFieldDto[];
        }
      >({
        query: ({ entityId, boardId, customFields }) => ({
          url: `/api/auth/${entityId}/board/${boardId}/custom_fields`,
          method: "PUT",
          data: {
            fields: customFields,
          },
        }),
        onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
          const patchResult = dispatch(
            tasksApi.util.updateQueryData(
              "getUserTaskBoards",
              { entityId: args.entityId },
              (draftState) => {
                const matchingBoard = draftState.find(
                  (board) => board.id === args.boardId
                );

                if (!matchingBoard) {
                  return;
                }

                matchingBoard.customFields = args.customFields.map(
                  transformTaskCustomFieldDtoToTaskCustomFieldModel
                );
              }
            )
          );

          queryFulfilled.catch(patchResult.undo);
        },
      }),

      deleteTaskCustomField: builder.mutation<
        void,
        { entityId: string; boardId: string; customFieldId: string }
      >({
        query: ({ entityId, boardId, customFieldId }) => ({
          url: `/api/auth/${entityId}/board/${boardId}/custom_fields/${customFieldId}`,
          method: "DELETE",
        }),
        onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
          const patchResult = dispatch(
            tasksApi.util.updateQueryData(
              "getUserTaskBoards",
              { entityId: args.entityId },
              (draftState) => {
                const matchingBoard = draftState.find(
                  (board) => board.id === args.boardId
                );

                if (!matchingBoard) {
                  return;
                }

                matchingBoard.customFields = matchingBoard.customFields.filter(
                  (field) => field.id !== args.customFieldId
                );
              }
            )
          );

          queryFulfilled.catch(patchResult.undo);
        },
      }),

      updateGlobalTaskCustomFieldsInBulk: builder.mutation<
        TasksInitData["custom_fields"],
        {
          entityId: string;
          customFieldsId: string;
          customFields: TaskCustomFieldDto[];
        }
      >({
        query: ({ entityId, customFieldsId, customFields }) => ({
          url: `/api/auth/entity/${entityId}/v2/task/custom_fields/${customFieldsId}`,
          method: "PUT",
          data: { props: { value: customFields } },
        }),
        onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
          const patchResult = dispatch(
            tasksApi.util.updateQueryData(
              "taskInit",
              { entityId: args.entityId },
              (taskInitDraft) => {
                taskInitDraft.customFieldsData.items = args.customFields.map(
                  transformTaskCustomFieldDtoToTaskCustomFieldModel
                );
              }
            )
          );

          queryFulfilled.catch(patchResult.undo);
        },
      }),

      createTaskLabel: builder.mutation<
        TaskLabel,
        {
          entityId: string;
          label: Pick<TaskLabel, "text" | "color" | "order">;
        }
      >({
        query: ({ entityId, label }) => ({
          url: `/api/auth/entity/${entityId}/labels`,
          method: "POST",
          data: label,
        }),
        onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
          try {
            const { data: newLabel } = await queryFulfilled;

            dispatch(
              tasksApi.util.updateQueryData(
                "taskInit",
                { entityId: args.entityId },
                (taskInitDraft) => {
                  taskInitDraft.labels.push({
                    ...newLabel,
                    color: newLabel.color || DEFAULT_LABEL_COLOR,
                  });
                }
              )
            );
          } catch {
            // Do nothing
          }
        },
      }),

      updateTaskLabel: builder.mutation<
        TaskLabel,
        { entityId: string; label: TaskLabel }
      >({
        query: ({ entityId, label }) => {
          const { id: labelId, ...labelWithoutId } = label;

          return {
            url: `/api/auth/entity/${entityId}/labels/${labelId}`,
            method: "PUT",
            data: labelWithoutId,
          };
        },
        onQueryStarted: (args, { dispatch, queryFulfilled }) => {
          const patchResult = dispatch(
            tasksApi.util.updateQueryData(
              "taskInit",
              { entityId: args.entityId },
              (taskInitDraft) => {
                const labelIndexToUpdate = taskInitDraft.labels.findIndex(
                  (label) => label.id === args.label.id
                );

                taskInitDraft.labels[labelIndexToUpdate] = args.label;
              }
            )
          );

          queryFulfilled.catch(patchResult.undo);
        },
      }),

      updateTaskLabelsInBulk: builder.mutation<
        { data: TaskLabel[] },
        { entityId: string; labels: TaskLabel[] }
      >({
        query: ({ entityId, labels }) => ({
          url: `/api/auth/entity/${entityId}/labels`,
          method: "PUT",
          data: { labels },
        }),
        onQueryStarted: (args, { dispatch, queryFulfilled }) => {
          const patchResult = dispatch(
            tasksApi.util.updateQueryData(
              "taskInit",
              { entityId: args.entityId },
              (taskInitDraft) => {
                taskInitDraft.labels = args.labels;
              }
            )
          );

          queryFulfilled.catch(patchResult.undo);
        },
      }),

      deleteTaskLabel: builder.mutation<
        void,
        { entityId: string; labelId: string }
      >({
        query: ({ entityId, labelId }) => ({
          url: `/api/auth/entity/${entityId}/labels/${labelId}`,
          method: "DELETE",
        }),
        onQueryStarted: (args, { dispatch, queryFulfilled }) => {
          const patchResult = dispatch(
            tasksApi.util.updateQueryData(
              "taskInit",
              { entityId: args.entityId },
              (taskInitDraft) => {
                const labelIndexToDelete = taskInitDraft.labels.findIndex(
                  (label) => label.id === args.labelId
                );

                if (labelIndexToDelete > -1) {
                  taskInitDraft.labels.splice(labelIndexToDelete, 1);
                }
              }
            )
          );

          queryFulfilled.catch(patchResult.undo);
        },
      }),

      createTaskCustomStatus: builder.mutation<
        TaskCustomStatusDto,
        {
          entityId: string;
          boardId: string;
          customStatus: {
            text: string;
            color: string;
            order: number;
          };
        }
      >({
        query: ({ entityId, boardId, customStatus }) => ({
          url: `/api/auth/entity/${entityId}/v2/task/custom_statuses`,
          method: "POST",
          data: { ...customStatus, board_id: boardId },
        }),
        onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
          try {
            const { data: customStatus } = await queryFulfilled;

            dispatch(
              tasksApi.util.updateQueryData(
                "getUserTaskBoards",
                { entityId: args.entityId },
                (draftState) => {
                  const matchingBoard = draftState.find(
                    (board) => board.id === args.boardId
                  );

                  if (!matchingBoard) {
                    return;
                  }

                  matchingBoard.customStatuses.push(customStatus);
                }
              )
            );
          } catch {
            // Do nothing
          }
        },
      }),

      updateTaskCustomStatuses: builder.mutation<
        TaskCustomStatusDto[],
        {
          entityId: string;
          boardId: string;
          customStatuses: TaskCustomStatusDto[];
        }
      >({
        query: ({ entityId, boardId, customStatuses }) => ({
          url: `/api/auth/entity/${entityId}/v2/task/custom_statuses`,
          method: "PUT",
          data: { statuses: customStatuses, board_id: boardId },
        }),
        onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
          const patchResult = dispatch(
            tasksApi.util.updateQueryData(
              "getUserTaskBoards",
              { entityId: args.entityId },
              (draftState) => {
                const matchingBoard = draftState.find(
                  (board) => board.id === args.boardId
                );

                if (!matchingBoard) {
                  return;
                }

                matchingBoard.customStatuses = args.customStatuses;
              }
            )
          );

          queryFulfilled.catch(patchResult.undo);
        },
      }),

      deleteTaskCustomStatus: builder.mutation<
        void,
        {
          entityId: string;
          boardId: string;
          statusId: string;
          altStatusId: string;
        }
      >({
        query: ({ entityId, boardId, statusId, altStatusId }) => ({
          url: `/api/auth/entity/${entityId}/v2/task/custom_statuses/${statusId}`,
          method: "DELETE",
          params: {
            alt_status: altStatusId,
            board_id: boardId,
          },
        }),
        onQueryStarted: (args, { dispatch, queryFulfilled }) => {
          const deleteStatusPatchResult = dispatch(
            tasksApi.util.updateQueryData(
              "getUserTaskBoards",
              { entityId: args.entityId },
              (draftState) => {
                const matchingBoard = draftState.find(
                  (board) => board.id === args.boardId
                );

                if (!matchingBoard) {
                  return;
                }

                matchingBoard.customStatuses =
                  matchingBoard.customStatuses.filter(
                    (status) => status.id !== args.statusId
                  );
              }
            )
          );

          queryFulfilled.catch(deleteStatusPatchResult.undo);
        },
        invalidatesTags: (_result, error, { boardId }) => {
          if (error || !boardId) {
            return [];
          }

          return [getTaskByBoardIdTag(boardId)];
        },
      }),

      createTaskFilterSet: builder.mutation<
        TaskFilterSet,
        { entityId: string; name: string; filters: InternalTaskFilters }
      >({
        query: ({ entityId, name, filters }) => ({
          url: `/api/auth/entity/${entityId}/task_filter_sets`,
          method: "POST",
          data: {
            name,
            ...adaptInternalTaskFiltersToTaskFilters(filters),
          },
        }),
        onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
          try {
            const { data: newFilterSet } = await queryFulfilled;

            dispatch(
              tasksApi.util.updateQueryData(
                "taskInit",
                { entityId: args.entityId },
                (taskInitDraft) => {
                  taskInitDraft.filterSets.push(
                    adaptTaskFilterSetToInternalTaskFilterSet(newFilterSet)
                  );
                }
              )
            );
          } catch {
            // Do nothing
          }
        },
      }),

      updateTaskFilterSet: builder.mutation<
        TaskFilterSet,
        {
          entityId: string;
          filterSetId: string;
          name: string;
          filters: InternalTaskFilters;
        }
      >({
        query: ({ entityId, filterSetId, name, filters }) => ({
          url: `/api/auth/entity/${entityId}/task_filter_sets/${filterSetId}`,
          method: "PUT",
          data: {
            name,
            ...adaptInternalTaskFiltersToTaskFilters(filters),
          },
        }),
        onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
          try {
            const { data: updatedFilterSet } = await queryFulfilled;

            dispatch(
              tasksApi.util.updateQueryData(
                "taskInit",
                { entityId: args.entityId },
                (taskInitDraft) => {
                  const filterSetIndexToUpdate =
                    taskInitDraft.filterSets.findIndex(
                      (filterSet) => filterSet.id === args.filterSetId
                    );

                  if (filterSetIndexToUpdate > -1) {
                    taskInitDraft.filterSets[filterSetIndexToUpdate] =
                      adaptTaskFilterSetToInternalTaskFilterSet(
                        updatedFilterSet
                      );
                  }
                }
              )
            );
          } catch {
            // Do nothing
          }
        },
      }),

      deleteTaskFilterSet: builder.mutation<
        void,
        { entityId: string; filterSetId: string }
      >({
        query: ({ entityId, filterSetId }) => ({
          url: `/api/auth/entity/${entityId}/task_filter_sets/${filterSetId}`,
          method: "DELETE",
        }),
        onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
          try {
            await queryFulfilled;

            dispatch(
              tasksApi.util.updateQueryData(
                "taskInit",
                { entityId: args.entityId },
                (taskInitDraft) => {
                  const filterSetIndexToDelete =
                    taskInitDraft.filterSets.findIndex(
                      (filterSet) => filterSet.id === args.filterSetId
                    );

                  if (filterSetIndexToDelete > -1) {
                    taskInitDraft.filterSets.splice(filterSetIndexToDelete, 1);
                  }
                }
              )
            );
          } catch {
            // Do nothing
          }
        },
      }),
      // #endregion

      // #region task activities
      getTaskActivities: builder.query<
        PaginatedResponse<TaskActivity>,
        {
          entityId: string;
          params: PaginatedRequestParams<{ task_id: string; type: string }>;
        }
      >({
        query: ({ entityId, params }) => ({
          url: `/api/auth/entity/${entityId}/task_activities`,
          params,
          silentError: true,
        }),
        providesTags: (_result, _error, { params }) => [
          { type: TasksApiTag.taskActivity, id: params.task_id },
        ],
      }),
      // #endregion

      // #region task time logs
      getTaskTimeLogs: builder.query<
        PaginatedResponse<TaskTimeLogModel>,
        {
          entityId: string;
          taskId: string;
          params?: PaginatedRequestParams<{ users?: string[] }>;
        }
      >({
        query: ({ entityId, taskId, params }) => ({
          url: `/api/auth/${entityId}/task/${taskId}/time_logs/list`,
          method: "POST",
          data: params,
        }),
        transformResponse: (response: PaginatedResponse<TaskTimeLogDto>) => ({
          ...response,
          data: response.data.map(transformTaskTimeLogDtoToModel),
        }),
      }),

      getActiveTimeLog: builder.query<
        ActiveTaskTimeLogModel | null,
        { entityId: string }
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/time_logs/active`,
        }),
        transformResponse: (response: ActiveTaskTimeLogDto | null) =>
          response ? transformActiveTaskTimeLogDtoToModel(response) : null,
        providesTags: [TasksApiTag.activeTimeLog],
      }),

      getMyTimeLogsSummary: builder.query<
        MyTimeLogsSummaryModel,
        { entityId: string; taskId: string }
      >({
        query: ({ entityId, taskId }) => ({
          url: `/api/auth/${entityId}/task/${taskId}/time_logs/summary`,
        }),
        transformResponse: (response: MyTimeLogsSummaryDto) =>
          transformMyTimeLogsSummaryDtoToModel(response),
        providesTags: [TasksApiTag.myTimeLogsSummary],
      }),

      createTaskTimeLog: builder.mutation<
        TaskTimeLogModel,
        {
          entityId: string;
          taskId: string;
          model: CreateTaskTimeLogModel;
        }
      >({
        query: ({ entityId, taskId, model }) => ({
          url: `/api/auth/${entityId}/task/${taskId}/time_logs`,
          method: "POST",
          data: transformCreateTaskTimeLogModelToDto(model),
        }),
        transformResponse: (response: TaskTimeLogDto) =>
          transformTaskTimeLogDtoToModel(response),
        invalidatesTags: [
          TasksApiTag.timeLog,
          TasksApiTag.activeTimeLog,
          TasksApiTag.myTimeLogsSummary,
        ],
      }),

      updateTaskTimeLog: builder.mutation<
        TaskTimeLogModel,
        {
          entityId: string;
          taskId: string;
          timeLogId: string;
          model: UpdateTaskTimeLogModel;
        }
      >({
        query: ({ entityId, taskId, timeLogId, model }) => ({
          url: `/api/auth/${entityId}/task/${taskId}/time_logs/${timeLogId}`,
          method: "PUT",
          data: transformUpdateTaskTimeLogModelToDto(model),
        }),
        transformResponse: (response: TaskTimeLogDto) =>
          transformTaskTimeLogDtoToModel(response),
        invalidatesTags: [
          TasksApiTag.timeLog,
          TasksApiTag.activeTimeLog,
          TasksApiTag.myTimeLogsSummary,
        ],
      }),

      deleteTaskTimeLog: builder.mutation<
        void,
        {
          entityId: string;
          taskId: string;
          timeLogId: string;
        }
      >({
        query: ({ entityId, taskId, timeLogId }) => ({
          url: `/api/auth/${entityId}/task/${taskId}/time_logs/${timeLogId}`,
          method: "DELETE",
        }),
        invalidatesTags: [
          TasksApiTag.timeLog,
          TasksApiTag.activeTimeLog,
          TasksApiTag.myTimeLogsSummary,
        ],
      }),

      getTimeLogs: builder.query<
        TimeLogListItemModel[],
        {
          entityId: string;
          boardId: SpecificOrAnyString<"my" | "team">;
          fromISO: string;
          toISO: string;
        }
      >({
        query: ({ entityId, boardId, fromISO, toISO }) => ({
          url: `/api/auth/${entityId}/time_logs/list`,
          method: "POST",
          data: {
            entity_id: entityId,
            board: boardId,
            from: fromISO,
            to: toISO,
          },
        }),
        transformResponse: (response: TimeLogListItemDto[]) =>
          filterOutInvalidTaskLikeObjects(response).map(
            transformTimeLogListItemDtoToModel
          ),
        providesTags: [TasksApiTag.timeLog],
      }),
      // #endregion

      getChecklistSummary: builder.query<
        { data: ChecklistSummary[] },
        {
          entityId: string;
          params: {
            // timestamp
            from: string;
            // timestamp
            to: string;
            // list of user_ids separated by comma(,)
            user_id: string;
          };
        }
      >({
        query: ({ entityId, params = {} }) => ({
          url: `api/auth/entity/${entityId}/checklists_summary`,
          params,
        }),
      }),

      // #region guest tasks
      getGuestTask: builder.query<
        GuestTaskDataModel,
        { taskShortId: string; isGuest: boolean }
      >({
        query: ({ taskShortId, isGuest }) => ({
          url: isGuest
            ? `/api/guest/task/${taskShortId}`
            : `/api/auth/guest/task/${taskShortId}`,
          silentError: true,
        }),
        transformResponse: (dto: GuestTaskDataDto) =>
          transformGuestDataDtoToModel(dto),
        providesTags: [TasksApiTag.taskActivity],
      }),

      getGuestSubtasks: builder.query<
        PaginatedResponse<SubtaskModel>,
        PaginatedRequestParams<{
          taskShortId: string;
          entityId: string;
          taskId: string;
          isGuest: boolean;
          searchQuery?: string;
        }>
      >({
        query: ({
          taskShortId,
          entityId,
          taskId,
          isGuest,
          searchQuery,
          ...params
        }) => ({
          url: isGuest
            ? `/api/guest/task/${taskShortId}/subtask`
            : `/api/auth/guest/task/${taskShortId}/subtask`,
          params: {
            entity_id: entityId,
            task_id: taskId,
            search_term: searchQuery ? `%${searchQuery}%` : undefined,
            ...params,
          },
          silentError: true,
        }),
        transformResponse: (response: PaginatedResponse<SubtaskDto>) => ({
          ...response,
          data: response.data.map(transformSubtaskDtoToModel),
        }),
      }),

      getGuestAttachments: builder.query<
        TaskAttachment[],
        {
          taskShortId: string;
          entityId: string;
          taskId: string;
          isGuest: boolean;
        }
      >({
        query: ({ taskShortId, entityId, taskId, isGuest }) => ({
          url: isGuest
            ? `/api/guest/task/${taskShortId}/attachments`
            : `/api/auth/guest/task/${taskShortId}/attachments`,
          params: {
            entity_id: entityId,
            task_id: taskId,
            type: "task",
          },
          silentError: true,
        }),
        transformResponse: (response: PaginatedResponse<TaskAttachment>) =>
          response.data,
      }),

      sendGuestTaskComment: builder.mutation<
        TaskActivity,
        {
          entityId: string;
          taskId: string;
          isGuest: boolean;
          activityName?: "guest_comment" | "guest_comment_reply";
          data: {
            content?: string;
            user_name?: string;
            files?: File[];
          };
        }
      >({
        query: ({
          entityId,
          taskId,
          isGuest,
          activityName = "guest_comment",
          data,
        }) => {
          const formData = new FormData();
          if (data.content) {
            formData.append("content", data.content);
          }
          if (isGuest && data.user_name) {
            formData.append("user_name", data.user_name);
          }
          if (activityName === "guest_comment_reply") {
            formData.append("action", activityName);
          } else {
            formData.append("activity_name", activityName);
          }

          data.files?.forEach((file) => {
            formData.append(
              data.files && data.files.length > 1 ? "files[]" : "files",
              file
            );
          });

          const url = isGuest
            ? `/api/entity/${entityId}/guest/task/${taskId}`
            : activityName === "guest_comment_reply"
            ? `/api/auth/entity/${entityId}/task/${taskId}/comments`
            : `/api/auth/entity/${entityId}/guest/task/${taskId}`;
          return {
            url,
            method: "POST",
            data: formData,
          };
        },
        invalidatesTags: [TasksApiTag.taskActivity],
      }),

      updateGuestTaskComment: builder.mutation<
        TaskActivity,
        {
          entityId: string;
          taskId: string;
          commentId: string;
          isGuest: boolean;
          data: {
            content?: string;
            removedFiles?: string[];
          };
        }
      >({
        query: ({ entityId, taskId, commentId, data }) => {
          const formData = new FormData();
          if (data.content) {
            formData.append("content", data.content);
          }
          formData.append("user_name", "guest");
          formData.append("activity_name", "guest_comment");
          data.removedFiles?.forEach((filePath) => {
            formData.append("removed_files[]", filePath);
          });

          return {
            url: `/api/auth/entity/${entityId}/guest/task/${taskId}/${commentId}`,
            method: "PUT",
            data: formData,
          };
        },
        invalidatesTags: [TasksApiTag.taskActivity],
      }),

      deleteGuestTaskComment: builder.mutation<
        void,
        {
          entityId: string;
          taskId: string;
          commentId: string;
        }
      >({
        query: ({ entityId, taskId, commentId }) => ({
          url: `/api/auth/entity/${entityId}/guest/task/${taskId}/${commentId}`,
          method: "DELETE",
        }),
        invalidatesTags: [TasksApiTag.taskActivity],
      }),

      sendGuestFeedback: builder.mutation<
        TaskActivity,
        {
          entityId: string;
          taskId: string;
          userName?: string;
          data: {
            rating: number;
            hashtag: string[];
            feedback: string;
          };
          isGuest: boolean;
        }
      >({
        query: ({ entityId, taskId, data, isGuest, userName }) => ({
          url: isGuest
            ? `/api/entity/${entityId}/guest/task/${taskId}`
            : `/api/auth/entity/${entityId}/guest/task/${taskId}`,
          method: "POST",
          data: {
            activity_name: "guest_feedback",
            user_name: isGuest ? userName : undefined,
            content: {
              ...data,
            },
          },
        }),
        invalidatesTags: [TasksApiTag.taskActivity],
      }),

      sendGuestTaskOpenedActivity: builder.mutation<
        TaskActivity,
        {
          entityId: string;
          taskId: string;
        }
      >({
        query: ({ entityId, taskId }) => ({
          url: `/api/auth/entity/${entityId}/guest/task/${taskId}`,
          method: "POST",
          data: {
            activity_name: "guest_opened",
          },
        }),
        invalidatesTags: [TasksApiTag.taskActivity],
      }),
      // #endregion

      // #region task boards
      getPreviewTaskBoard: builder.query<
        PreviewTaskBoardModel,
        { entityId: string; boardId: string }
      >({
        query: ({ entityId, boardId }) => ({
          url: `/api/auth/${entityId}/board/${boardId}`,
        }),
        transformResponse: (board: PreviewTaskBoardDto, _, args) =>
          transformPreviewTaskBoardDtoToModel(args.boardId, board),
        onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
          try {
            const { data: board } = await queryFulfilled;

            dispatch(
              tasksApi.util.updateQueryData(
                "getUserTaskBoards",
                { entityId: args.entityId },
                (draftState) => {
                  const matchingBoard = draftState.find(
                    (b) => b.id === args.boardId
                  );

                  if (matchingBoard) {
                    Object.assign(matchingBoard, board);
                    return;
                  }

                  draftState.push(
                    transformPreviewTaskBoardModelToTaskBoardModel(board)
                  );
                }
              )
            );
          } catch {
            // Do nothing
          }
        },
      }),

      getMultiplePreviewTaskBoards: builder.query<
        PreviewTaskBoardModel[],
        { entityId: string; boardIds: string[] }
      >({
        query: ({ entityId, boardIds }) => ({
          url: `/api/auth/${entityId}/board/list_by_ids`,
          method: "POST",
          data: { ids: boardIds },
        }),
        transformResponse: (boards: (PreviewTaskBoardDto & { id: string })[]) =>
          boards.map((board) =>
            transformPreviewTaskBoardDtoToModel(board.id, board)
          ),
        onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
          try {
            const { data: fetchedBoards } = await queryFulfilled;
            // Update the general task boards cache
            dispatch(
              tasksApi.util.updateQueryData(
                "getUserTaskBoards",
                { entityId: args.entityId },
                (draftState) => {
                  fetchedBoards.forEach((fetchedBoard) => {
                    const matchingBoard = draftState.find(
                      (board) => board.id === fetchedBoard.id
                    );

                    if (matchingBoard) {
                      Object.assign(matchingBoard, fetchedBoard);
                      return;
                    }

                    draftState.push(
                      transformPreviewTaskBoardModelToTaskBoardModel(
                        fetchedBoard
                      )
                    );
                  });
                }
              )
            );

            // Update particular preview task board cache entries
            fetchedBoards.forEach((fetchedBoard) => {
              dispatch(
                tasksApi.util.upsertQueryData(
                  "getPreviewTaskBoard",
                  {
                    entityId: args.entityId,
                    boardId: fetchedBoard.id,
                  },
                  fetchedBoard
                )
              );
            });
          } catch {
            // Do nothing
          }
        },
      }),

      getUserTaskBoards: builder.query<TaskBoardModel[], { entityId: string }>({
        queryFn: async (args, _queryApi, _extraOptions, baseQuery) => {
          const allBoards = await fetchAllPages<TaskBoardDto>(
            `/api/auth/${args.entityId}/board`,
            { page_size: 50 },
            baseQuery as RtkBaseQuery
          );

          return { data: allBoards.map(transformTaskBoardDtoToModel) };
        },
        providesTags: (_result, _error, { entityId }) => [
          getBoardByEntityIdTag(entityId),
        ],
        onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
          try {
            const { data: boards } = await queryFulfilled;

            boards.forEach((board) => {
              dispatch(
                tasksApi.util.upsertQueryData(
                  "getPreviewTaskBoard",
                  {
                    entityId: args.entityId,
                    boardId: board.id,
                  },
                  board
                )
              );
            });
          } catch {
            // Do nothing
          }
        },
      }),

      // TODO: Remove if not needed
      getEntityTaskBoards: builder.query<
        BasicTaskBoardModel[],
        { entityId: string }
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/board/lite`,
          params: {
            page_size: 100,
          },
        }),
        transformResponse: (response: PaginatedResponse<BasicTaskBoardDto>) =>
          response.data,
      }),

      createTaskBoard: builder.mutation<
        TaskBoardDto,
        {
          entityId: string;
          data: CreateTaskBoardPayload;
        }
      >({
        query: ({ entityId, data }) => ({
          url: `/api/auth/${entityId}/board`,
          method: "POST",
          data,
          silentError: true,
        }),
        invalidatesTags: (_result, _error, { entityId }) => [
          getBoardByEntityIdTag(entityId),
        ],
      }),

      updateTaskBoard: builder.mutation<
        TaskBoardDto,
        {
          entityId: string;
          boardId: string;
          data: UpdateTaskBoardPayload;
          optimistic?: boolean;
        }
      >({
        query: ({ entityId, boardId, data }) => ({
          url: `/api/auth/${entityId}/board/${boardId}`,
          method: "PUT",
          data,
          silentError: true,
        }),
        onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
          const updateCacheEntries = <TData extends Partial<TaskBoardModel>>(
            updatedAttributes: TData
          ) => {
            // Update the task boards list
            const taskBoardsPatchResult = dispatch(
              tasksApi.util.updateQueryData(
                "getUserTaskBoards",
                { entityId: args.entityId },
                (draftState) => {
                  const matchingBoard = draftState.find(
                    (board) => board.id === args.boardId
                  );

                  if (!matchingBoard) {
                    return;
                  }

                  Object.assign(matchingBoard, updatedAttributes);
                }
              )
            );

            // Update the preview task board
            const previewTaskBoardPatchResult = dispatch(
              tasksApi.util.updateQueryData(
                "getPreviewTaskBoard",
                {
                  entityId: args.entityId,
                  boardId: args.boardId,
                },
                (draftState) => {
                  Object.assign(draftState, updatedAttributes);
                }
              )
            );

            return [taskBoardsPatchResult, previewTaskBoardPatchResult];
          };

          if (args.optimistic) {
            const updatedAttributes: Partial<TaskBoardModel> = {
              ...transformPartialTaskBoardDtoToPartialTaskBoardModel(
                args.data as Partial<TaskBoardDto>
              ),
              // If the users list is provided, we need to transform it so it's compatible with the cached data
              users: args.data.users
                ? args.data.users.map((user) => ({
                    ...user,
                    updated_at: new Date().toISOString(),
                  }))
                : undefined,
            };

            const patchResults = updateCacheEntries(
              // Remove undefined entries to avoid overwriting the cached data
              omitUndefined(updatedAttributes)
            );

            queryFulfilled.catch(() => {
              patchResults.forEach((patchResult) => patchResult.undo());
            });
            return;
          }

          try {
            const { data: board } = await queryFulfilled;

            const updatedAttributes = omitUndefined(
              transformPartialTaskBoardDtoToPartialTaskBoardModel(board)
            );

            updateCacheEntries(updatedAttributes);
          } catch {
            // Do nothing
          }
        },
      }),

      deleteTaskBoard: builder.mutation<
        void,
        {
          entityId: string;
          boardId: string;
        }
      >({
        query: ({ entityId, boardId }) => ({
          url: `/api/auth/${entityId}/board/${boardId}`,
          method: "DELETE",
        }),
        invalidatesTags: (_result, _error, { entityId }) => [
          getBoardByEntityIdTag(entityId),
        ],
      }),

      getBoardTemplates: builder.query<
        BoardTemplatesCategories,
        {
          params: {
            type: "board";
          };
        }
      >({
        query: ({ params }) => ({
          url: `/api/auth/templates`,
          params,
        }),
      }),

      getTemplatesList: builder.query<
        BoardTemplatesList,
        {
          params: PaginatedRequestParams<{
            type: "board";
            category?: string;
            sub_category?: string;
            is_popular?: boolean;
            search_term?: string;
          }>;
        }
      >({
        query: ({ params }) => ({
          url: `/api/auth/templates`,
          params,
        }),
      }),

      getBoardTemplateDetails: builder.query<
        BoardTemplateDetails,
        {
          id: string;
        }
      >({
        query: ({ id }) => ({
          url: `/api/auth/templates/${id}`,
          method: "GET",
        }),
      }),

      createBoardFromTemplate: builder.mutation<
        {
          id: string;
        },
        {
          entityId: string;
          id: string;
        }
      >({
        query: ({ entityId, id }) => ({
          url: `/api/auth/templates/${entityId}/${id}`,
          method: "POST",
        }),
        invalidatesTags: (_result, _error, { entityId }) => [
          getBoardByEntityIdTag(entityId),
        ],
      }),

      // #endregion
    }),

    overrideExisting: false,
  });

  return tasksApi;
};

export type TasksApi = ReturnType<typeof addTasksApi>;
